import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Rocket, RefreshCw, CheckCircle2, XCircle, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function BatchDeploy() {
  const [domains, setDomains] = useState("");
  const [niche, setNiche] = useState("");
  const [skipAftermarket, setSkipAftermarket] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pollingBatch, setPollingBatch] = useState(null);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/batch-deploy/history`);
      setHistory(r.data.items || []);
    } catch (e) {
      toast.error("Blad historii: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  useEffect(() => {
    if (!pollingBatch) return;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/admin/batch-deploy/status/${pollingBatch}`);
        setHistory((h) => h.map((b) => b.id === pollingBatch ? r.data : b));
        if (r.data.status !== "processing") {
          setPollingBatch(null);
          toast.success(`Batch ${pollingBatch.slice(0,8)} zakonczony (${r.data.status})`);
          loadHistory();
        }
      } catch (e) {
        // ignore
      }
    }, 15000);
    return () => clearInterval(id);
  }, [pollingBatch]);

  const submit = async () => {
    const list = domains.split(/[\s,;\n]+/).map((d) => d.trim()).filter(Boolean);
    if (list.length === 0) {
      toast.error("Wpisz przynajmniej 1 domene");
      return;
    }
    if (list.length > 100) {
      toast.error("Max 100 domen w batchu");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/batch-deploy/submit`, {
        domains: list,
        niche: niche || null,
        skip_aftermarket: skipAftermarket,
      });
      toast.success(`Batch uruchomiony (${r.data.domains} domen). ETA ~15 min/domena.`);
      setDomains("");
      setNiche("");
      setPollingBatch(r.data.batch_id);
      await loadHistory();
    } catch (e) {
      toast.error("Blad startu: " + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (s) => {
    if (s === "processing") return <Badge className="bg-blue-600"><Clock className="w-3 h-3 mr-1" />Trwa...</Badge>;
    if (s === "completed") return <Badge className="bg-emerald-600"><CheckCircle2 className="w-3 h-3 mr-1" />Zrobione</Badge>;
    if (s === "failed") return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Blad</Badge>;
    return <Badge>{s}</Badge>;
  };

  const resultBadge = (r) => {
    if (r.status === "deployed" || r.status === "ok") return <span className="text-emerald-600">✓ {r.domain}</span>;
    if (r.status === "failed") return <span className="text-red-600">✗ {r.domain}: {r.error?.slice(0, 60)}</span>;
    return <span className="text-slate-600">⋯ {r.domain}: {r.status}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-2">
            <Rocket className="w-8 h-8 text-blue-500" />
            Batch Deploy — automatyczne generowanie stron
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Wpisz liste domen. Agent sekwencyjnie: DNS → deploy VPS #2 → SSL → sync do Epidemos →
            AI analiza (kategoria/cennik) → auto_publish → fetch kategorii → post-deploy QA.
            ETA ~15 min/domena.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Nowy batch</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-xs uppercase text-muted-foreground">Domeny (po przecinku, spacjami lub w nowych liniach — max 100)</label>
            <Textarea
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
              placeholder="foo.pl bar.pl baz.pl&#10;qux.pl"
              rows={5}
              className="mt-1 font-mono text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase text-muted-foreground">Nisza (opcjonalna hint dla AI)</label>
              <Input
                value={niche}
                onChange={(e) => setNiche(e.target.value)}
                placeholder="np. finanse, gastronomia, sport"
                className="mt-1"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={skipAftermarket} onCheckedChange={(v) => setSkipAftermarket(!!v)} />
                <span className="text-sm">Pomin sprawdzenie aftermarket (jesli domena juz Twoja)</span>
              </label>
            </div>
          </div>

          <Button onClick={submit} disabled={submitting || !domains.trim()} size="lg">
            {submitting ? "Startowanie..." : (
              <><Rocket className="w-4 h-4 mr-2" /> Uruchom agenta</>
            )}
          </Button>

          <p className="text-xs text-muted-foreground">
            Agent robi automatycznie: (1) sprawdza aftermarket i dodaje DNS do VPS #2, (2) deploy portalu (nginx + SSL + systemd), (3) sync do Epidemos jako hunted site + Publisher API key, (4) Grok analiza (nazwa marki, target, kategoria, cennik) i wlacza auto_publish, (5) GSC bootstrap, (6) pobiera kategorie WP z portalu, (7) post-deploy QA (homepage/logo/favicon/sitemap/llms/artykul/covery) z auto-retry.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Historia batchy</span>
            <Button onClick={loadHistory} variant="outline" size="sm" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Odswiez
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">Brak batchy w historii.</p>
          ) : (
            <div className="space-y-3">
              {history.map((b) => {
                const ok = (b.results || []).filter((r) => r.status === "deployed" || r.status === "ok").length;
                const total = (b.domains || []).length;
                return (
                  <div key={b.id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <code className="text-xs">{b.id.slice(0, 8)}</code>
                        <span className="ml-3 text-sm text-muted-foreground">
                          {new Date(b.created_at).toLocaleString("pl-PL")}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {statusBadge(b.status)}
                        <span className="text-sm font-medium">{ok}/{total} OK</span>
                      </div>
                    </div>
                    {b.niche && <div className="text-xs text-muted-foreground mb-1">Nisza: {b.niche}</div>}
                    <div className="text-xs space-y-0.5">
                      {(b.results && b.results.length > 0) ? b.results.map((r, i) => (
                        <div key={i}>{resultBadge(r)}</div>
                      )) : (
                        <div className="text-slate-500">Domeny: {(b.domains || []).join(", ")}</div>
                      )}
                    </div>
                    {b.status === "processing" && b.id !== pollingBatch && (
                      <Button size="sm" variant="outline" onClick={() => setPollingBatch(b.id)} className="mt-2">
                        Sledz na zywo
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
